import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { toast } from "sonner";
import { Camera, Eye, EyeOff, Check, Loader2, Plug, BookOpen, Users, User, Mail, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useDomainGlossary } from "@/hooks/use-domain-glossary-tips";
import { TeamTab } from "@/components/workspace/TeamTab";
import { ProfileTab } from "@/components/settings/ProfileTab";
import { EmailTab } from "@/components/settings/EmailTab";
import { PasswordTab } from "@/components/settings/PasswordTab";

interface Integration {
  id: string;
  provider: string;
  api_key: string;
  enabled: boolean;
  config: any;
}

const PROVIDERS = [
  {
    id: "companycam",
    name: "CompanyCam",
    icon: Camera,
    description: "Sync progress photos and share access with contractors",
    helpUrl: "https://app.companycam.com/settings/integrations",
  },
];

function IntegrationCard({ provider, integration, onSave, onToggle }: {
  provider: typeof PROVIDERS[number];
  integration: Integration | null;
  onSave: (provider: string, apiKey: string) => Promise<void>;
  onToggle: (id: string, enabled: boolean) => Promise<void>;
}) {
  const [apiKey, setApiKey] = useState(integration?.api_key || "");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!apiKey.trim()) { toast.error("API key is required"); return; }
    setSaving(true);
    try {
      await onSave(provider.id, apiKey);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader className="flex flex-row items-center gap-3 space-y-0">
        <div className="p-2 rounded-lg bg-muted">
          <provider.icon className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <CardTitle className="text-base">{provider.name}</CardTitle>
          <CardDescription>{provider.description}</CardDescription>
        </div>
        {integration && (
          <Badge variant={integration.enabled ? "default" : "secondary"}>
            {integration.enabled ? "Connected" : "Disabled"}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>API Key</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="Enter your API key..."
              />
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Find your API key at{" "}
          <a href={provider.helpUrl} target="_blank" rel="noopener" className="text-primary hover:underline">
            {provider.name} Settings
          </a>
        </p>
        {integration && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggle(integration.id, !integration.enabled)}
          >
            {integration.enabled ? "Disable" : "Enable"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { currentWorkspace, isAdmin } = useWorkspace();
  const { showDomainTips, setShowDomainTips } = useDomainGlossary();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchIntegrations = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("workspace_integrations")
        .select("*")
        .eq("workspace_id", currentWorkspace.id);
      if (error) throw error;
      setIntegrations((data || []) as unknown as Integration[]);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);

  const saveIntegration = useCallback(async (provider: string, apiKey: string) => {
    if (!currentWorkspace?.id) return;
    const existing = integrations.find(i => i.provider === provider);
    if (existing) {
      const { error } = await supabase
        .from("workspace_integrations")
        .update({ api_key: apiKey } as any)
        .eq("id", existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from("workspace_integrations")
        .insert({
          workspace_id: currentWorkspace.id,
          provider,
          api_key: apiKey,
        } as any);
      if (error) throw error;
    }
    toast.success("Integration saved");
    await fetchIntegrations();
  }, [currentWorkspace?.id, integrations, fetchIntegrations]);

  const toggleIntegration = useCallback(async (id: string, enabled: boolean) => {
    const { error } = await supabase
      .from("workspace_integrations")
      .update({ enabled } as any)
      .eq("id", id);
    if (error) throw error;
    toast.success(enabled ? "Integration enabled" : "Integration disabled");
    await fetchIntegrations();
  }, [fetchIntegrations]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your workspace and preferences</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="team"><Users className="h-4 w-4 mr-1" /> Team</TabsTrigger>
          <TabsTrigger value="integrations"><Plug className="h-4 w-4 mr-1" /> Integrations</TabsTrigger>
          <TabsTrigger value="profile"><User className="h-4 w-4 mr-1" /> Profile</TabsTrigger>
          <TabsTrigger value="email"><Mail className="h-4 w-4 mr-1" /> Email</TabsTrigger>
          <TabsTrigger value="password"><Lock className="h-4 w-4 mr-1" /> Password</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                Terminology
              </CardTitle>
              <CardDescription>
                Glossary data lives in <code className="text-xs bg-muted px-1 rounded">src/data/domainGlossary.json</code>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 p-4">
                <div className="space-y-1">
                  <Label htmlFor="domain-tips" className="text-base">Show domain term hints</Label>
                  <p className="text-sm text-muted-foreground max-w-prose">
                    When enabled, a small <span className="text-foreground">ⓘ</span> icon appears next to abbreviations and
                    key metrics (FMR, HAP, cap rate, and Section 8 deal score rows). Hover or focus the icon to read
                    business definitions. Turning this off removes every hint in the app for your browser.
                  </p>
                </div>
                <Switch
                  id="domain-tips"
                  checked={showDomainTips}
                  onCheckedChange={setShowDomainTips}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="space-y-4">
          <TeamTab />
        </TabsContent>

        <TabsContent value="integrations">
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Integrations</h2>
              <p className="text-sm text-muted-foreground">Connect third-party services to your workspace. Each workspace manages its own API keys.</p>
            </div>
            {!isAdmin && (
              <Card className="shadow-soft border-amber-500/20">
                <CardContent className="py-4">
                  <p className="text-sm text-amber-600">Only workspace admins can manage integrations.</p>
                </CardContent>
              </Card>
            )}
            {PROVIDERS.map(provider => (
              <IntegrationCard
                key={provider.id}
                provider={provider}
                integration={integrations.find(i => i.provider === provider.id) || null}
                onSave={saveIntegration}
                onToggle={toggleIntegration}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="profile" className="space-y-4">
          <ProfileTab />
        </TabsContent>

        <TabsContent value="email" className="space-y-4">
          <EmailTab />
        </TabsContent>

        <TabsContent value="password" className="space-y-4">
          <PasswordTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
